import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { clearReservations, fetchReservations, setFilters } from '../redux/slices/reservationsSlice';
import { toast } from 'react-toastify';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import MenuItem from '@mui/material/MenuItem';
import ReservationsTable from '../components/reservations/ReservationsTable';
import { DEFAULT_PAGE, DEFAULT_ROWS_PER_PAGE } from '../utils/paginationUtils';

export default function Reservations() {
  const { t } = useTranslation();

  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();

  const reservations = useSelector((state) => state.reservations.data);
  const reservationsError = useSelector((state) => state.reservations.error);
  const isLoading = useSelector((state) => state.reservations.isLoading);

  const totalItems = useSelector((state) => state.reservations.totalItems);
  const options = useSelector((state) => state.reservations.options);

  const from = useMemo(() => (options.from ? dayjs(options.from) : null), [options.from]);
  const to = useMemo(() => (options.to ? dayjs(options.to) : null), [options.to]);

  useEffect(() => {
    return () => {
      dispatch(clearReservations());
    };
  }, [dispatch]);

  useEffect(() => {
    const filtersFromUrl = {
      page: parseInt(searchParams.get('page')) || DEFAULT_PAGE,
      limit: parseInt(searchParams.get('limit')) || DEFAULT_ROWS_PER_PAGE,
      search: searchParams.get('search') || '',
      status: searchParams.get('status') || 'ALL',
      from: searchParams.get('from'),
      to: searchParams.get('to'),
    };

    dispatch(setFilters(filtersFromUrl));
    dispatch(fetchReservations(filtersFromUrl));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, searchParams.get('page'), searchParams.get('limit')]);

  useEffect(() => {
    setSearchParams(
      {
        page: options.page,
        limit: options.limit,
        search: options.search || '',
        status: options.status || 'ALL',
        from: options.from,
        to: options.to,
      },
      { replace: true }
    );
  }, [options.page, options.limit, options.search, options.status, options.from, options.to, setSearchParams]);

  const updateFilters = (patch) => {
    const updated = { ...options, ...patch };
    setSearchParams(updated);
  };

  const handleOnSearchClick = () => updateFilters({ page: 0 });
  const handleChangePage = (_, newPage) => updateFilters({ page: newPage + 1 });
  const handleChangeRowsPerPage = (e) => updateFilters({ limit: +e.target.value, page: 1 });

  useEffect(() => {
    if (reservationsError) {
      toast.error(t('An error occured, please refresh the page and try again!'), {
        position: toast.POSITION.BOTTOM_RIGHT,
      });
    }
  }, [reservationsError, t]);

  const searchHelperText = t(
    'Search using reservation id, reservation ref, order id, event id, name, email, phone or notes'
  );

  return (
    <div className="orders">
      <Typography variant="h3">{t('Reservations')}</Typography>
      <Box sx={{ mt: 4 }}>
        <Paper sx={{ p: 3, mb: 3 }}>
          <Grid container alignItems="center" spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField
                onChange={(event) => dispatch(setFilters({ search: event.target.value }))}
                value={options.search}
                sx={{ marginRight: 4 }}
                inputProps={{ autoComplete: 'off' }}
                label={t('Search')}
                fullWidth
                placeholder={searchHelperText}
                variant="outlined"
                type="text"
              />
            </Grid>
            <Grid item xs={12} md>
              <DateTimePicker
                disableFuture
                maxDateTime={to}
                onChange={(value) => dispatch(setFilters({ from: value.toISOString() }))}
                value={from}
                sx={{ marginRight: 3 }}
                slotProps={{ textField: { label: t('From'), fullWidth: true, variant: 'outlined', error: false } }}
              />
            </Grid>
            <Grid item xs={12} md>
              <DateTimePicker
                disableFuture
                minDateTime={from}
                onChange={(value) => dispatch(setFilters({ to: value.toISOString() }))}
                value={to}
                sx={{ marginRight: 3 }}
                slotProps={{ textField: { label: t('To'), fullWidth: true, variant: 'outlined', error: false } }}
              />
            </Grid>
            <Grid item xs={12} md>
              <TextField
                onChange={(event) => dispatch(setFilters({ status: event.target.value }))}
                value={options.status}
                sx={{ marginRight: 3 }}
                label={t('Status')}
                fullWidth
                select
                variant="outlined"
                type="text"
              >
                <MenuItem key={0} value={'ALL'}>
                  {t('All')}
                </MenuItem>
                <MenuItem key={1} value={'PENDING'}>
                  {t('PENDING')}
                </MenuItem>
                <MenuItem key={2} value={'COMPLETED'}>
                  {t('COMPLETED')}
                </MenuItem>
                <MenuItem key={3} value={'EXPIRED'}>
                  {t('EXPIRED')}
                </MenuItem>
                <MenuItem key={4} value={'CANCELED'}>
                  {t('CANCELED')}
                </MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} md>
              <div>
                <Button disabled={isLoading} fullWidth onClick={handleOnSearchClick} size="large" variant="contained">
                  {t('Search')}
                  {isLoading && <CircularProgress size={24} sx={{ ml: 1, color: 'white' }} />}
                </Button>
              </div>
            </Grid>
          </Grid>
        </Paper>
        <ReservationsTable
          reservations={reservations}
          pagination={{
            totalItems,
            page: options.page,
            limit: options.limit,
            onPageChange: handleChangePage,
            onRowsPerPageChange: handleChangeRowsPerPage,
          }}
        />
      </Box>
    </div>
  );
}
